import {
  CUSTOM_SIZE_MAX,
  CUSTOM_SIZE_MIN,
  FORMAT_PRESETS,
  type FormatPreset,
  PRESET_CATEGORY_LABELS,
} from "@cva/shared";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Check, CircleAlert, ExternalLink, Loader2, MinusCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { client, orpc } from "../lib/api";
import { cn, errorMessage } from "../lib/utils";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter } from "./ui/dialog";

/**
 * „In mehrere Formate“: one copy of a design per chosen format (Post → Story,
 * LinkedIn, A4 …) in the same project, via the existing format transfer.
 * Copies are created one after the other; the plan limit stops the rest.
 */

interface Target {
  key: string;
  presetId: string;
  label: string;
  width?: number;
  height?: number;
}

type Status =
  | { state: "pending" }
  | { state: "running" }
  | { state: "done"; documentId: string; title: string }
  | { state: "error"; message: string }
  | { state: "skipped" };

/** Label without the aspect ratio in brackets: „Story (9:16)“ → „Story“. */
export function shortFormatLabel(label: string): string {
  return label.replace(/\s*\([^)]*\)\s*$/, "").trim() || label;
}

function sizeLabel(preset: FormatPreset): string {
  return preset.print
    ? `${preset.print.widthMm} × ${preset.print.heightMm} mm`
    : `${preset.width} × ${preset.height} px`;
}

function clampSize(value: number): number {
  if (!Number.isFinite(value)) return CUSTOM_SIZE_MIN;
  return Math.round(Math.max(CUSTOM_SIZE_MIN, Math.min(CUSTOM_SIZE_MAX, value)));
}

export function MultiFormatDialog({
  orgId,
  documentId,
  title,
  currentPreset,
  open,
  onOpenChange,
  beforeTransfer,
}: {
  orgId: string;
  documentId: string;
  title: string;
  currentPreset?: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Waits until the editor's changes reached the server. */
  beforeTransfer?: () => Promise<unknown>;
}) {
  const queryClient = useQueryClient();
  const presets = FORMAT_PRESETS.filter(
    (preset) => preset.kind === "design" && preset.id !== currentPreset,
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [custom, setCustom] = useState(false);
  const [customWidth, setCustomWidth] = useState(1200);
  const [customHeight, setCustomHeight] = useState(1200);
  const [targets, setTargets] = useState<Target[]>([]);
  const [status, setStatus] = useState<Record<string, Status>>({});
  const [running, setRunning] = useState(false);

  // A fresh start each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setCustom(false);
    setTargets([]);
    setStatus({});
  }, [open]);

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const chosen: Target[] = [
    ...presets
      .filter((preset) => selected.has(preset.id))
      .map((preset) => ({ key: preset.id, presetId: preset.id, label: preset.label })),
    ...(custom
      ? [
          {
            key: "custom",
            presetId: "custom",
            label: `Eigene Größe ${clampSize(customWidth)} × ${clampSize(customHeight)} px`,
            width: clampSize(customWidth),
            height: clampSize(customHeight),
          },
        ]
      : []),
  ];

  const start = async () => {
    const list = chosen;
    if (list.length === 0) return;
    setTargets(list);
    setStatus(Object.fromEntries(list.map((target) => [target.key, { state: "pending" }])));
    setRunning(true);
    try {
      await beforeTransfer?.();
      let stopped = false;
      for (const target of list) {
        if (stopped) {
          setStatus((current) => ({ ...current, [target.key]: { state: "skipped" } }));
          continue;
        }
        setStatus((current) => ({ ...current, [target.key]: { state: "running" } }));
        const name =
          target.presetId === "custom"
            ? `${title} – ${target.width} × ${target.height}`
            : `${title} – ${shortFormatLabel(target.label)}`;
        try {
          const created = await client.documents.transfer({
            orgId,
            documentId,
            presetId: target.presetId,
            title: name.slice(0, 200),
            ...(target.width && target.height
              ? { width: target.width, height: target.height }
              : {}),
          });
          setStatus((current) => ({
            ...current,
            [target.key]: { state: "done", documentId: created.id, title: created.title },
          }));
        } catch (error) {
          setStatus((current) => ({
            ...current,
            [target.key]: { state: "error", message: errorMessage(error) },
          }));
          // The plan limit applies to all further copies too.
          const code = (error as { code?: string } | null)?.code;
          if (code === "QUOTA_EXCEEDED" || code === "FORBIDDEN" || code === "TOO_MANY_REQUESTS") {
            stopped = true;
          }
        }
      }
    } catch (error) {
      setStatus((current) => {
        const next = { ...current };
        for (const target of list) {
          if (next[target.key]?.state === "pending") {
            next[target.key] = { state: "error", message: errorMessage(error) };
          }
        }
        return next;
      });
    } finally {
      setRunning(false);
      void queryClient.invalidateQueries({ queryKey: orpc.documents.key() });
    }
  };

  const categories = ["social", "print"] as const;
  const started = targets.length > 0;
  const doneCount = Object.values(status).filter((entry) => entry.state === "done").length;

  return (
    <Dialog open={open} onOpenChange={(next) => !running && onOpenChange(next)}>
      <DialogContent
        title="In mehrere Formate übertragen"
        description="Legt für jedes gewählte Format eine eigene Kopie im selben Projekt an. Inhalte werden proportional skaliert und neu verteilt; das Original bleibt unverändert."
        className="max-w-3xl"
      >
        {!started ? (
          <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
            {categories.map((category) => (
              <div key={category}>
                <p className="mb-2 text-xs font-semibold text-zinc-700">
                  {PRESET_CATEGORY_LABELS[category]}
                </p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {presets
                    .filter((preset) => preset.category === category)
                    .map((preset) => {
                      const active = selected.has(preset.id);
                      return (
                        <label
                          key={preset.id}
                          className={cn(
                            "flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 transition",
                            active
                              ? "border-brand-500 bg-brand-50 ring-2 ring-brand-500/20"
                              : "border-zinc-200 hover:border-zinc-300",
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={active}
                            onChange={() => toggle(preset.id)}
                            className="mt-0.5 accent-brand-600"
                          />
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-medium">
                              {preset.label}
                            </span>
                            <span className="block text-xs text-zinc-500">{sizeLabel(preset)}</span>
                          </span>
                        </label>
                      );
                    })}
                </div>
              </div>
            ))}
            <div>
              <p className="mb-2 text-xs font-semibold text-zinc-700">Eigene Größe</p>
              <div
                className={cn(
                  "flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2",
                  custom ? "border-brand-500 bg-brand-50" : "border-zinc-200",
                )}
              >
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input
                    type="checkbox"
                    checked={custom}
                    onChange={() => setCustom(!custom)}
                    className="accent-brand-600"
                  />
                  Eigene Größe
                </label>
                <label className="flex items-center gap-1 text-xs text-zinc-500">
                  Breite
                  <input
                    type="number"
                    min={CUSTOM_SIZE_MIN}
                    max={CUSTOM_SIZE_MAX}
                    value={customWidth}
                    onChange={(event) => {
                      setCustomWidth(Number(event.target.value));
                      setCustom(true);
                    }}
                    className="h-8 w-20 rounded-md border border-zinc-200 bg-white px-2 text-sm text-zinc-800"
                  />
                </label>
                <label className="flex items-center gap-1 text-xs text-zinc-500">
                  Höhe
                  <input
                    type="number"
                    min={CUSTOM_SIZE_MIN}
                    max={CUSTOM_SIZE_MAX}
                    value={customHeight}
                    onChange={(event) => {
                      setCustomHeight(Number(event.target.value));
                      setCustom(true);
                    }}
                    className="h-8 w-20 rounded-md border border-zinc-200 bg-white px-2 text-sm text-zinc-800"
                  />
                </label>
                <span className="text-xs text-zinc-500">px</span>
              </div>
            </div>
          </div>
        ) : (
          <ul className="max-h-[60vh] space-y-1.5 overflow-y-auto">
            {targets.map((target) => {
              const entry = status[target.key] ?? { state: "pending" };
              return (
                <li
                  key={target.key}
                  className="flex items-center gap-3 rounded-lg border border-zinc-200 px-3 py-2 text-sm"
                >
                  <span className="shrink-0 [&_svg]:size-4">
                    {entry.state === "done" ? (
                      <Check className="text-emerald-600" />
                    ) : entry.state === "error" ? (
                      <CircleAlert className="text-red-600" />
                    ) : entry.state === "skipped" ? (
                      <MinusCircle className="text-zinc-400" />
                    ) : entry.state === "running" ? (
                      <Loader2 className="animate-spin text-brand-600" />
                    ) : (
                      <span className="block size-4 rounded-full border border-zinc-300" />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{target.label}</span>
                    <span
                      className={cn(
                        "block truncate text-xs",
                        entry.state === "error" ? "text-red-600" : "text-zinc-500",
                      )}
                    >
                      {entry.state === "done"
                        ? `„${entry.title}“ angelegt`
                        : entry.state === "error"
                          ? entry.message
                          : entry.state === "skipped"
                            ? "Übersprungen"
                            : entry.state === "running"
                              ? "Wird angelegt …"
                              : "Wartet"}
                    </span>
                  </span>
                  {entry.state === "done" ? (
                    <Link
                      to="/o/$orgId/documents/$documentId"
                      params={{ orgId, documentId: entry.documentId }}
                      onClick={() => onOpenChange(false)}
                      className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 [&_svg]:size-3.5"
                    >
                      Öffnen <ExternalLink />
                    </Link>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
        <DialogFooter>
          {started ? (
            <>
              <span className="mr-auto self-center text-sm text-zinc-500">
                {running
                  ? `${doneCount} von ${targets.length} angelegt …`
                  : `${doneCount} von ${targets.length} Dateien angelegt`}
              </span>
              <Button disabled={running} onClick={() => onOpenChange(false)}>
                Fertig
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Abbrechen
              </Button>
              <Button disabled={chosen.length === 0} onClick={() => void start()}>
                {chosen.length <= 1 ? "Datei anlegen" : `${chosen.length} Dateien anlegen`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
